import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rewardApi } from '../../api/reward.api';
import { PieceEconomyResponse, TopHolderItem } from '../../types/Reward.api.type';
import { Card } from '../ui/card';
import { Coins, TrendingUp, TrendingDown, ArrowRightLeft, AlertTriangle, Crown, Users } from 'lucide-react';

/** 리워드 종류 한국어 */
const TYPE_LABELS: Record<string, string> = {
  VOTE: '투표',
  VOTE_AD_BONUS: '투표광고보너스',
  DAILY_BONUS: '일일 보너스',
  DAILY_AD_BONUS: '오늘의투표광고보너스',
  WEEKLY_MILESTONE_1: '주간 1단계',
  WEEKLY_MILESTONE_2: '주간 2단계',
  WEEKLY_MILESTONE_3: '주간 3단계',
  WEEKLY_MILESTONE_4: '주간 4단계',
  CREATOR_MILESTONE: '설문 작성 보너스',
  FIRST_VOTE_BONUS: '첫 투표 보너스',
  MILESTONE_AD_BONUS: '누적투표보너스',
  ATTENDANCE: '출석',
  ATTENDANCE_AD_BONUS: '출석광고보너스',
  ATTENDANCE_STREAK: '연속출석보너스',
  ATTENDANCE_STREAK_SAVE: '연속출석유지보너스',
  COMMENT: '댓글 보상',
  COMMENT_AD_BONUS: '댓글 광고보너스',
  SURVEY_LIKE: '설문 좋아요 보상',
  SURVEY_LIKE_AD_BONUS: '설문 좋아요 광고보너스',
  COMMENT_LIKE: '댓글 좋아요 보상',
  COMMENT_LIKE_AD_BONUS: '댓글 좋아요 광고보너스',
  WEEKLY_MISSION: '이번주 미션 완료',
  WEEKLY_MISSION_AD_BONUS: '이번주 미션 광고보너스',
};

/** 숫자 포맷 */
const fmt = (n: number) => n.toLocaleString('ko-KR');

export const PieceEconomyDashboard = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['adminPieceEconomy'],
    queryFn: () => rewardApi.getPieceEconomy().then((res) => res.data.results as PieceEconomyResponse),
    refetchInterval: 60000, // 1분 자동 갱신
  });

  if (isLoading || !data) {
    return <div className="text-center py-12 text-gray-400">조각 경제 데이터 로딩 중...</div>;
  }

  return (
    <div className="space-y-6">
      {/* ===== 핵심 요약 카드 3열 ===== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* 발행 */}
        <Card className="border-l-4 border-l-amber-400">
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-amber-600">
              <Coins className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">발행</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{fmt(data.totalIssued)}개</p>
              <p className="text-xs text-gray-500">총 발행 조각</p>
            </div>
            <div className="flex justify-between text-sm">
              <div>
                <p className="text-gray-500">오늘 발행</p>
                <p className="font-semibold text-amber-600">+{fmt(data.todayIssued)}개</p>
              </div>
              <div className="text-right">
                <p className="text-gray-500">토스포인트 직접</p>
                <p className="font-semibold text-blue-600">
                  {fmt(data.tossPointRewardAmount)}원 ({data.tossPointRewardCount}건)
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* 유통 */}
        <Card className="border-l-4 border-l-green-400">
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-green-600">
              <Users className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">유통</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{fmt(data.circulating)}개</p>
              <p className="text-xs text-gray-500">유통 중 조각 (전체 잔액)</p>
            </div>
            <div className="flex justify-between text-sm">
              <div>
                <p className="text-gray-500">평균 보유</p>
                <p className="font-semibold">{fmt(data.avgBalance)}개</p>
              </div>
              <div className="text-right">
                <p className="text-gray-500">보유 유저</p>
                <p className="font-semibold">
                  {fmt(data.holdersCount)}명 / {fmt(data.totalUserCount)}명
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* 전환 */}
        <Card className="border-l-4 border-l-blue-400">
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-blue-600">
              <ArrowRightLeft className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">전환</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{fmt(data.totalExchanged)}개</p>
              <p className="text-xs text-gray-500">전환된 조각 → {fmt(data.totalTossAmount)}원</p>
            </div>
            <div className="flex justify-between text-sm">
              <div>
                <p className="text-gray-500">오늘 전환</p>
                <p className="font-semibold text-blue-600">+{fmt(data.todayExchanged)}개</p>
              </div>
              <div className="text-right">
                <p className="text-gray-500">전환율</p>
                <p className="font-semibold">{data.exchangeRate}%</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* ===== 건전성 지표 ===== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatMini
          label="환전 성공률"
          value={`${data.exchangeSuccessRate}%`}
          sub={`${data.exchangeSuccessCount}/${data.exchangeTotalCount}건`}
          color="green"
        />
        <StatMini
          label="환전 실패"
          value={`${data.exchangeFailedCount}건`}
          sub="총 실패 건수"
          color={data.exchangeFailedCount > 0 ? 'red' : 'gray'}
        />
        <StatMini
          label="리워드 지급 실패"
          value={`${data.rewardFailedCount}건`}
          sub="총 실패 건수"
          color={data.rewardFailedCount > 0 ? 'red' : 'gray'}
        />
        <StatMini
          label="유통 비율"
          value={`${data.totalIssued > 0 ? Math.round((data.circulating / data.totalIssued) * 100) : 0}%`}
          sub="유통/발행"
          color="purple"
        />
      </div>

      {/* ===== 일별 추이 (7일) ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 발행 추이 */}
        <Card>
          <div className="p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-500" />
              일별 발행 추이 (7일)
            </h3>
            {data.dailyIssuanceTrend.length === 0 ? (
              <p className="text-center py-4 text-gray-400 text-sm">데이터 없음</p>
            ) : (
              <MiniBarChart
                data={data.dailyIssuanceTrend.map((d) => ({
                  label: d.date.slice(5),
                  value: d.amount,
                  sub: `${d.count}건`,
                }))}
                color="amber"
                unit="개"
              />
            )}
          </div>
        </Card>

        {/* 전환 추이 */}
        <Card>
          <div className="p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-blue-500" />
              일별 전환 추이 (7일)
            </h3>
            {data.dailyExchangeTrend.length === 0 ? (
              <p className="text-center py-4 text-gray-400 text-sm">데이터 없음</p>
            ) : (
              <MiniBarChart
                data={data.dailyExchangeTrend.map((d) => ({
                  label: d.date.slice(5),
                  value: d.pieceAmount,
                  sub: `${d.count}건 · ${fmt(d.tossAmount)}원`,
                }))}
                color="blue"
                unit="개"
              />
            )}
          </div>
        </Card>
      </div>

      {/* ===== 하단: TOP 10 보유/적립 + 종류별 ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* TOP 10 보유/적립 순위 */}
        <TopRankingCard data={data} />

        {/* 종류별 발행 비율 */}
        <PieceByTypeCard data={data} />
      </div>

      {/* 위험 알림 */}
      {(data.rewardFailedCount > 0 || data.exchangeFailedCount > 0) && (
        <Card className="border-red-200 bg-red-50">
          <div className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-red-700">주의 필요</p>
              {data.rewardFailedCount > 0 && (
                <p className="text-red-600">리워드 지급 실패 {data.rewardFailedCount}건</p>
              )}
              {data.exchangeFailedCount > 0 && <p className="text-red-600">환전 실패 {data.exchangeFailedCount}건</p>}
              <p className="text-red-500 mt-1">히스토리 탭에서 상세 내역을 확인하세요.</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

/** 종류별 조각 발행 — 오늘/전체 토글 (기본 오늘) */
const PieceByTypeCard = ({ data }: { data: PieceEconomyResponse }) => {
  const [scope, setScope] = useState<'today' | 'all'>('today');
  // 오늘 데이터는 구버전 응답 호환 위해 optional — 없으면 전체로 폴백
  const todayList = data.pieceByTypeToday;
  const isToday = scope === 'today' && todayList != null;
  const list = isToday ? (todayList as typeof data.pieceByType) : data.pieceByType;
  const denom = isToday ? data.todayIssued : data.totalIssued;

  return (
    <Card>
      <div className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Coins className="h-4 w-4 text-amber-500" />
            종류별 조각 발행
          </h3>
          <div className="flex gap-1">
            {(
              [
                { value: 'today', label: '오늘' },
                { value: 'all', label: '전체' },
              ] as const
            ).map((tab) => (
              <button
                key={tab.value}
                onClick={() => setScope(tab.value)}
                disabled={tab.value === 'today' && todayList == null}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                  scope === tab.value ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          {list.map((item) => {
            const pct = denom > 0 ? Math.round((item.totalAmount / denom) * 100) : 0;
            return (
              <div key={item.rewardType} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{TYPE_LABELS[item.rewardType] ?? item.rewardType}</span>
                  <span className="font-medium">
                    {fmt(item.totalAmount)}개 <span className="text-gray-400">({item.totalCount}건)</span>
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-100">
                  <div
                    className="h-2 rounded-full bg-amber-400 transition-all"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
          {list.length === 0 && (
            <p className="py-4 text-center text-sm text-gray-400">
              {isToday ? '오늘 발행 내역이 없습니다.' : '데이터 없음'}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};

/** 미니 통계 카드 */
const StatMini = ({ label, value, sub, color }: { label: string; value: string; sub: string; color: string }) => {
  const colors: Record<string, string> = {
    green: 'text-green-600',
    red: 'text-red-600',
    gray: 'text-gray-600',
    purple: 'text-purple-600',
    blue: 'text-blue-600',
  };
  return (
    <Card>
      <div className="p-3">
        <p className="text-xs text-gray-500 mb-0.5">{label}</p>
        <p className={`text-lg font-bold ${colors[color] ?? 'text-gray-900'}`}>{value}</p>
        <p className="text-xs text-gray-400">{sub}</p>
      </div>
    </Card>
  );
};

/** TOP 10 보유/적립 순위 카드 */
const TopRankingCard = ({ data }: { data: PieceEconomyResponse }) => {
  const [tab, setTab] = useState<'holders' | 'earners'>('holders');
  const list: TopHolderItem[] = tab === 'holders' ? data.topHolders : data.topEarners;
  const highlightKey = tab === 'holders' ? 'balance' : 'totalEarned';
  const highlightLabel = tab === 'holders' ? '보유' : '적립';

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Crown className="w-4 h-4 text-yellow-500" />
            조각 TOP 10
          </h3>
          <div className="flex rounded-lg border overflow-hidden">
            <button
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                tab === 'holders' ? 'bg-amber-500 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
              onClick={() => setTab('holders')}
            >
              보유순위
            </button>
            <button
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                tab === 'earners' ? 'bg-amber-500 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
              onClick={() => setTab('earners')}
            >
              적립순위
            </button>
          </div>
        </div>
        <div className="space-y-2">
          {list.map((holder, idx) => (
            <div key={holder.userId} className="flex items-center justify-between py-1.5 border-b last:border-0">
              <div className="flex items-center gap-2">
                <RankBadge rank={idx + 1} />
                <div>
                  <p className="text-sm font-medium">{holder.nickname}</p>
                  <p className="text-xs text-gray-400">ID: {holder.userId}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-amber-600">{fmt(holder[highlightKey])}개</p>
                <p className="text-xs text-gray-400">
                  {tab === 'holders'
                    ? `적립 ${fmt(holder.totalEarned)} · 전환 ${fmt(holder.totalExchanged)}`
                    : `보유 ${fmt(holder.balance)} · 전환 ${fmt(holder.totalExchanged)}`}
                </p>
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-center py-4 text-gray-400 text-sm">{highlightLabel}자 없음</p>}
        </div>
      </div>
    </Card>
  );
};

/** 순위 배지 (1-3위 금/은/동, 4-10위 회색) */
const RankBadge = ({ rank }: { rank: number }) => {
  const style =
    rank === 1
      ? 'bg-yellow-100 text-yellow-700'
      : rank === 2
        ? 'bg-gray-100 text-gray-600'
        : rank === 3
          ? 'bg-orange-100 text-orange-600'
          : 'bg-gray-50 text-gray-400';

  return (
    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${style}`}>{rank}</span>
  );
};

/** 간단한 바 차트 (Recharts 없이 CSS) */
const MiniBarChart = ({
  data,
  color,
  unit,
}: {
  data: { label: string; value: number; sub: string }[];
  color: string;
  unit: string;
}) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  const barColor = color === 'amber' ? 'bg-amber-400' : 'bg-blue-400';

  return (
    <div className="space-y-1.5">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-2">
          <span className="text-xs text-gray-500 w-12 text-right flex-shrink-0">{d.label}</span>
          <div className="flex-1 flex items-center gap-2">
            <div className="flex-1 bg-gray-100 rounded-full h-5 relative overflow-hidden">
              <div
                className={`${barColor} h-5 rounded-full transition-all flex items-center`}
                style={{ width: `${Math.max((d.value / max) * 100, 2)}%` }}
              >
                {d.value > 0 && d.value / max > 0.3 && (
                  <span className="text-xs text-white font-medium pl-2">
                    {fmt(d.value)}
                    {unit}
                  </span>
                )}
              </div>
              {d.value > 0 && d.value / max <= 0.3 && (
                <span className="absolute left-[calc(100%*0.3+4px)] top-0.5 text-xs text-gray-500">
                  {fmt(d.value)}
                  {unit}
                </span>
              )}
            </div>
          </div>
          <span className="text-xs text-gray-400 w-24 text-right flex-shrink-0">{d.sub}</span>
        </div>
      ))}
    </div>
  );
};
